import { arrayOfAll, assertNever, type MandateProps } from '@gitbook/util-ts';

import {
    type PageLayoutOptions,
    PageLayoutOptionsCoverSize,
    PageLayoutOptionsSchema,
    PageLayoutOptionsWidth,
    type PageLayoutOptionsWithDefaults,
    PageLayoutPreset,
    type RevisionPageDocument,
    type RevisionPageDocumentCover,
} from '../types';

export const PAGE_TITLE_LIMIT = 100;
export const PAGE_DEFAULT_TITLE = 'Untitled';

export const PAGE_DESCRIPTION_LIMIT = 200;

export const PAGE_COVER_HEIGHT_DEFAULT = 240;
export const PAGE_COVER_HEIGHT_MIN = 10;
export const PAGE_COVER_HEIGHT_MAX = 700;

function stripLineBreaks(str: string): string {
    return str.replace(/(\r\n|\r|\n)/g, ' ');
}

/**
 * Normalize a page title.
 */
export function normalizePageTitle(title: string, options?: { fallback?: string }): string {
    const defaultTitle =
        typeof options?.fallback === 'string' ? options.fallback : PAGE_DEFAULT_TITLE;
    return stripLineBreaks(title).trim().slice(0, PAGE_TITLE_LIMIT).trim() || defaultTitle;
}

/**
 * Normalize a page description.
 */
export function normalizePageDescription(description: string): string {
    return stripLineBreaks(description).trim().slice(0, PAGE_DESCRIPTION_LIMIT).trim();
}

/**
 * Validate that a layout input value is valid predefined layout preset
 */
export function validatePageLayoutPreset(layout: string): layout is PageLayoutPreset {
    const ALL_PAGE_LAYOUTS = arrayOfAll<PageLayoutPreset>()([
        PageLayoutPreset.Docs,
        PageLayoutPreset.Editorial,
        PageLayoutPreset.Landing,
    ]);
    return ALL_PAGE_LAYOUTS.includes(layout as PageLayoutPreset);
}

/**
 * Validate that a layout object have valid custom layout options
 */
export function validatePageCustomLayout(layout: object): layout is PageLayoutOptions {
    return (
        typeof layout !== 'undefined' &&
        PageLayoutOptionsSchema.required().safeParse(layout).success
    );
}

/**
 * Check if a page has a cover.
 */
export function hasPageCover(
    page: RevisionPageDocument
): page is RevisionPageDocument & { cover: MandateProps<RevisionPageDocumentCover, 'yPos'> } {
    return typeof page.cover?.yPos === 'number';
}

/**
 * Get page layout preset or fall back to default.
 * @default BasePageLayoutPreset.Docs
 */
export function getPageLayoutPreset(
    page: RevisionPageDocument | null
): PageLayoutPreset | 'custom' {
    if (!page?.layout) {
        return PageLayoutPreset.Docs;
    }

    return typeof page.layout === 'string' && validatePageLayoutPreset(page.layout)
        ? page.layout
        : 'custom';
}

/**
 * Get the layout options currently set for a page.
 */
export function getPageLayoutOptions(
    page: RevisionPageDocument | null
): PageLayoutOptionsWithDefaults {
    const layoutPreset = getPageLayoutPreset(page);
    const baseLayoutOptions: Record<PageLayoutPreset, PageLayoutOptionsWithDefaults> = {
        docs: {
            cover: true,
            coverSize: PageLayoutOptionsCoverSize.Full,
            width: PageLayoutOptionsWidth.Default,
            title: true,
            description: true,
            tableOfContents: true,
            outline: true,
            pagination: true,
            metadata: true,
        },
        editorial: {
            cover: true,
            coverSize: PageLayoutOptionsCoverSize.Full,
            width: PageLayoutOptionsWidth.Default,
            title: true,
            description: true,
            tableOfContents: false,
            outline: true,
            pagination: true,
            metadata: true,
        },
        landing: {
            cover: true,
            coverSize: PageLayoutOptionsCoverSize.Full,
            width: PageLayoutOptionsWidth.Default,
            title: true,
            description: true,
            tableOfContents: false,
            outline: false,
            pagination: true,
            metadata: false,
        },
    };

    switch (layoutPreset) {
        case PageLayoutPreset.Docs:
            return baseLayoutOptions.docs;
        case PageLayoutPreset.Editorial:
            return baseLayoutOptions.editorial;
        case PageLayoutPreset.Landing:
            return baseLayoutOptions.landing;
        case 'custom':
            return typeof page?.layout === 'object'
                ? normalizeLayoutOptionsWithDefaults(page.layout)
                : baseLayoutOptions.docs;
        default:
            assertNever(layoutPreset);
    }
}

/**
 * Normalize layout options with default values for missing properties.
 */
export function normalizeLayoutOptionsWithDefaults(
    layoutOptions: PageLayoutOptions
): PageLayoutOptionsWithDefaults {
    const defaults: PageLayoutOptionsWithDefaults = {
        width: PageLayoutOptionsWidth.Default,
        cover: true,
        coverSize: PageLayoutOptionsCoverSize.Full,
        title: true,
        description: true,
        tableOfContents: true,
        outline: true,
        pagination: true,
        metadata: true,
    };

    return Object.keys(defaults).reduce((acc, key) => {
        if (typeof layoutOptions[key] === 'undefined') {
            acc[key] = defaults[key];
        } else {
            acc[key] = layoutOptions[key];
        }
        return acc;
    }, {} as PageLayoutOptionsWithDefaults);
}
